import { useState, useCallback, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import {
  AlertCircle,
  ArrowLeft,
  Award,
  BadgeCheck,
  Ban,
  Calendar,
  CalendarClock,
  CheckCircle,
  ClipboardCheck,
  Eye,
  MapPin,
  MoreVertical,
  Shield,
  ShieldCheck,
  User as UserIcon,
} from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Modal } from '@/components/ui/Modal'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown'
import { Paginator } from '@/components/ui/Paginator'
import { Checkbox } from '@/components/ui/Checkbox'
import { Select } from '@/components/ui/Select'
import { useNotifications } from '@/contexts/NotificationContext'
import { useUser } from '@/hooks/useUsers'
import { useLicenseList } from '@/hooks/useLicenses'
import { useProgrammesByIds } from '@/hooks/useProgrammesByIds'
import { LicenseModel, LicenseStatus } from '@/models/license.model'
import { licensesService } from '@/services/licensesService'
import { paths } from '@/routes/path'
import { cn } from '@/utils/cn'

type ModalType =
  | 'recordExam'
  | 'scheduleInterview'
  | 'recordInterview'
  | 'issueLicense'
  | 'withdrawLicense'
  | 'viewLicense'

interface ModalState {
  type: ModalType | null
  license: LicenseModel | null
}

interface RecordExamFormValues {
  examScore: string
  examTakenAt: string
}

interface ScheduleInterviewFormValues {
  scheduledAt: string
  zoomLink: string
}

interface InterviewResultFormValues {
  score: string
  passed: string
}

interface IssueLicenseFormValues {
  score: string
  passed: string
}

interface WithdrawLicenseFormValues {
  reason: string
  revokeCertificate: boolean
}

const STATUS_LABELS: Record<LicenseStatus, string> = {
  exam_pending: 'Exam Pending',
  exam_passed_waiting_3m: 'Exam Passed · Awaiting Interview',
  interview_scheduled: 'Interview Scheduled',
  interview_passed: 'Interview Passed',
  interview_failed: 'Interview Failed',
  license_issued: 'License Issued',
  failed: 'Failed',
}

const STATUS_HINTS: Record<LicenseStatus, string> = {
  exam_pending: 'Awaiting exam result entry.',
  exam_passed_waiting_3m: 'Schedule an interview 3 months after exam.',
  interview_scheduled: 'Interview is on the calendar.',
  interview_passed: 'Interview passed, license can be issued.',
  interview_failed: 'Interview failed. Review results and decide next steps.',
  license_issued: 'License already issued to learner.',
  failed: 'Candidate failed current stage.',
}

const STATUS_STYLES: Record<LicenseStatus, string> = {
  exam_pending: 'bg-secondary-100 text-secondary-800 border border-secondary-200',
  exam_passed_waiting_3m: 'bg-amber-50 text-amber-700 border border-amber-200',
  interview_scheduled: 'bg-sky-50 text-sky-700 border border-sky-200',
  interview_passed: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  interview_failed: 'bg-rose-50 text-rose-700 border border-rose-200',
  license_issued: 'bg-primary-50 text-primary-700 border border-primary-200',
  failed: 'bg-rose-50 text-rose-700 border border-rose-200',
}

const DEFAULT_STATUS_CLASS = 'bg-secondary-100 text-secondary-700 border border-secondary-200'
const DEFAULT_STATUS_HINT = 'Status update pending.'

const humanizeStatus = (status: string) =>
  status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const resolveStatusLabel = (status?: string | null): string => {
  if (!status) return 'Pending'
  const typed = status as LicenseStatus
  return STATUS_LABELS[typed] ?? humanizeStatus(status)
}

const resolveStatusHint = (status?: string | null): string => {
  if (!status) return DEFAULT_STATUS_HINT
  const typed = status as LicenseStatus
  return STATUS_HINTS[typed] ?? `Current status: ${resolveStatusLabel(status)}. Review recent actions for next steps.`
}

const resolveStatusClass = (status?: string | null): string => {
  if (!status) return DEFAULT_STATUS_CLASS
  const typed = status as LicenseStatus
  return STATUS_STYLES[typed] ?? DEFAULT_STATUS_CLASS
}

const formatDate = (value: string | null) => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value))
  } catch {
    return value
  }
}

const toDateInputValue = (value: string | null) => {
  if (!value) return ''
  try {
    return new Date(value).toISOString().slice(0, 10)
  } catch {
    return value.slice(0, 10)
  }
}

const formatScore = (value: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return `${value.toFixed(2).replace(/\.00$/, '')}%`
}

export function UserLicensesPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(5)
  const [modalState, setModalState] = useState<ModalState>({ type: null, license: null })
  const [modalError, setModalError] = useState<string | null>(null)

  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
  } = useUser(userId)

  const {
    data: licensesResponse,
    isLoading: isLicensesLoading,
    isFetching: isLicensesFetching,
    isError: isLicensesError,
    error: licensesError,
  } = useLicenseList({
    userId: userId ?? '',
    page,
    perPage,
  })

  const licenses = licensesResponse?.data ?? []
  const meta = licensesResponse?.meta

  const programmeIds = useMemo(() => {
    const ids = licenses
      .map((license) => license.programmeId)
      .filter((id): id is string => Boolean(id))
    return Array.from(new Set(ids)).sort()
  }, [licenses])

  const { data: programmeMap } = useProgrammesByIds(programmeIds)

  const recordExamForm = useForm<RecordExamFormValues>({
    defaultValues: {
      examScore: '',
      examTakenAt: '',
    },
  })

  const scheduleInterviewForm = useForm<ScheduleInterviewFormValues>({
    defaultValues: {
      scheduledAt: '',
      zoomLink: '',
    },
  })

  const interviewResultForm = useForm<InterviewResultFormValues>({
    defaultValues: {
      score: '',
      passed: 'true',
    },
  })

  const issueLicenseForm = useForm<IssueLicenseFormValues>({
    defaultValues: {
      score: '',
      passed: 'true',
    },
  })

  const withdrawLicenseForm = useForm<WithdrawLicenseFormValues>({
    defaultValues: {
      reason: '',
      revokeCertificate: true,
    },
  })

  const minInterviewDate = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() + 1)
    return date.toISOString().slice(0, 10)
  }, [])

  const closeModal = () => {
    setModalState({ type: null, license: null })
    setModalError(null)
  }

  const resolveProgrammeName = useCallback(
    (programmeId?: string | null, fallback?: string | null, includeIdFallback: boolean = true) => {
      const mappedName = programmeId ? programmeMap?.[programmeId]?.title : null
      if (mappedName) return mappedName
      if (fallback) return fallback
      return includeIdFallback ? programmeId ?? '—' : '—'
    },
    [programmeMap],
  )

  const openModal = useCallback((type: ModalType, license: LicenseModel | null = null) => {
    setModalState({ type, license })
    setModalError(null)

    if (type === 'recordExam') {
      recordExamForm.reset({
        examScore: license?.examScore ? String(license.examScore) : '',
        examTakenAt: toDateInputValue(license?.examTakenAt ?? null),
      })
    }

    if (type === 'scheduleInterview') {
      scheduleInterviewForm.reset({
        scheduledAt: toDateInputValue(license?.interviewScheduledAt ?? null),
        zoomLink: license?.interviewZoomLink ?? '',
      })
    }

    if (type === 'recordInterview') {
      interviewResultForm.reset({
        score: license?.interviewScore ? String(license.interviewScore) : '',
        passed: String(Boolean(license?.interviewPassed)),
      })
    }

    if (type === 'issueLicense') {
      issueLicenseForm.reset({
        score: license?.interviewScore ? String(license.interviewScore) : '',
        passed: String(Boolean(license?.interviewPassed ?? true)),
      })
    }

    if (type === 'withdrawLicense') {
      withdrawLicenseForm.reset({
        reason: '',
        revokeCertificate: true,
      })
    }
  }, [issueLicenseForm, interviewResultForm, recordExamForm, scheduleInterviewForm, userId, withdrawLicenseForm])

  const invalidateLicenses = () => {
    queryClient.invalidateQueries({ queryKey: ['licenses'] })
  }

  const notify = (title: string, message: string) => {
    addNotification({
      title,
      message,
      type: 'general',
      priority: 'medium',
      isRead: false,
      isArchived: false,
    })
  }

  const recordExamMutation = useMutation({
    mutationFn: async (payload: { examScore: number; examTakenAt?: string }) => {
      if (!userId) {
        throw new Error('User ID is required')
      }
      return licensesService.recordExamResult(userId, {
        examScore: payload.examScore,
        examTakenAt: payload.examTakenAt,
      })
    },
    onSuccess: (license) => {
      closeModal()
      invalidateLicenses()
      notify('Exam recorded', `Exam result saved for license ${license.licenseReference ?? license.id ?? ''}.`)
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to record exam result'
      notify('Exam recording failed', message)
      setModalError(message)
    },
  })

  const scheduleInterviewMutation = useMutation({
    mutationFn: async (payload: { licenseId: string; scheduledAt: string; zoomLink?: string }) => {
      return licensesService.scheduleInterview(payload.licenseId, {
        scheduledAt: payload.scheduledAt,
        zoomLink: payload.zoomLink,
      })
    },
    onSuccess: () => {
      closeModal()
      invalidateLicenses()
      notify('Interview scheduled', 'Interview details saved successfully.')
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to schedule interview'
      notify('Scheduling failed', message)
      setModalError(message)
    },
  })

  const interviewResultMutation = useMutation({
    mutationFn: async (payload: { licenseId: string; score: number; passed: boolean }) => {
      return licensesService.recordInterviewResult(payload.licenseId, {
        score: payload.score,
        passed: payload.passed,
      })
    },
    onSuccess: () => {
      closeModal()
      invalidateLicenses()
      notify('Interview recorded', 'Interview outcome saved.')
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to record interview result'
      notify('Interview recording failed', message)
      setModalError(message)
    },
  })

  const issueLicenseMutation = useMutation({
    mutationFn: async (payload: { licenseId: string; score?: number; passed?: boolean }) => {
      return licensesService.issueLicense(payload.licenseId, {
        score: payload.score,
        passed: payload.passed,
      })
    },
    onSuccess: () => {
      closeModal()
      invalidateLicenses()
      notify('License issued', 'License has been issued successfully.')
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to issue license'
      notify('License issuance failed', message)
      setModalError(message)
    },
  })

  const withdrawLicenseMutation = useMutation({
    mutationFn: async (payload: { licenseId: string; reason: string; revokeCertificate: boolean }) => {
      return licensesService.withdrawLicense(payload.licenseId, {
        reason: payload.reason,
        revokeCertificate: payload.revokeCertificate,
      })
    },
    onSuccess: (response) => {
      closeModal()
      invalidateLicenses()
      notify(
        'License withdrawn',
        response?.licenseId
          ? `License ${response.licenseId} withdrawn${response.revokedCertificatesCount ? ` · ${response.revokedCertificatesCount} certificate(s) revoked` : ''}.`
          : 'License withdrawn successfully.',
      )
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to withdraw license'
      notify('Withdrawal failed', message)
      setModalError(message)
    },
  })

  const handleRecordExamSubmit = recordExamForm.handleSubmit((values) => {
    if (!userId) {
      notify('Missing learner', 'User ID is required to record an exam result.')
      setModalError('User ID is required to record an exam result.')
      return
    }
    const score = Number(values.examScore)
    if (Number.isNaN(score)) {
      recordExamForm.setError('examScore', { message: 'Enter a valid score' })
      return
    }
    recordExamMutation.mutate({
      examScore: score,
      examTakenAt: values.examTakenAt || undefined,
    })
  })

  const handleScheduleInterviewSubmit = scheduleInterviewForm.handleSubmit((values) => {
    if (!modalState.license?.id) return
    const selectedDate = new Date(values.scheduledAt)
    const minDate = new Date(minInterviewDate)
    if (selectedDate < minDate) {
      scheduleInterviewForm.setError('scheduledAt', { message: 'Select a future date.' })
      setModalError('Interview date must be in the future.')
      return
    }
    scheduleInterviewMutation.mutate({
      licenseId: modalState.license.id,
      scheduledAt: values.scheduledAt,
      zoomLink: values.zoomLink || undefined,
    })
  })

  const handleInterviewResultSubmit = interviewResultForm.handleSubmit((values) => {
    if (!modalState.license?.id) return
    const score = Number(values.score)
    if (Number.isNaN(score)) {
      interviewResultForm.setError('score', { message: 'Enter a valid score' })
      return
    }
    interviewResultMutation.mutate({
      licenseId: modalState.license.id,
      score,
      passed: values.passed === 'true',
    })
  })

  const handleIssueLicenseSubmit = issueLicenseForm.handleSubmit((values) => {
    if (!modalState.license?.id) return
    const score = values.score ? Number(values.score) : undefined
    if (values.score && Number.isNaN(score)) {
      issueLicenseForm.setError('score', { message: 'Enter a valid score' })
      return
    }
    issueLicenseMutation.mutate({
      licenseId: modalState.license.id,
      score,
      passed: values.passed ? values.passed === 'true' : undefined,
    })
  })

  const handleWithdrawLicenseSubmit = withdrawLicenseForm.handleSubmit((values) => {
    if (!modalState.license?.id) return
    withdrawLicenseMutation.mutate({
      licenseId: modalState.license.id,
      reason: values.reason,
      revokeCertificate: values.revokeCertificate,
    })
  })

  const licenseActionItems = useCallback((license: LicenseModel): DropdownItem[] => {
    const status = license.status as LicenseStatus | undefined
    const canIssue = Boolean(license.interviewPassed) && !license.licenseIssued
    const canWithdraw = Boolean(license.licenseIssued)

    return [
      {
        label: 'View details',
        value: 'view',
        icon: <Eye className="h-3.5 w-3.5" />,
      },
      {
        label: 'Schedule interview',
        value: 'schedule',
        icon: <CalendarClock className="h-3.5 w-3.5 text-sky-600" />,
        disabled: status === 'license_issued' || status === 'failed',
      },
      {
        label: 'Record interview result',
        value: 'record-interview',
        icon: <ClipboardCheck className="h-3.5 w-3.5 text-emerald-600" />,
        disabled: !license.interviewScheduledAt,
      },
      {
        label: 'Issue license',
        value: 'issue',
        icon: <Award className="h-3.5 w-3.5 text-primary-600" />,
        disabled: !canIssue,
      },
      {
        label: 'Withdraw license',
        value: 'withdraw',
        icon: <Ban className="h-3.5 w-3.5 text-rose-600" />,
        disabled: !canWithdraw,
      },
    ]
  }, [])

  const handleLicenseActionSelect = useCallback((license: LicenseModel, action: string) => {
    if (!license?.id || !action) return
    switch (action) {
      case 'view':
        openModal('viewLicense', license)
        break
      case 'schedule':
        openModal('scheduleInterview', license)
        break
      case 'record-interview':
        openModal('recordInterview', license)
        break
      case 'issue':
        openModal('issueLicense', license)
        break
      case 'withdraw':
        openModal('withdrawLicense', license)
        break
      default:
        break
    }
  }, [openModal])

  const isLoading = isUserLoading || isLicensesLoading
  const isEmpty = !isLicensesLoading && !licenses.length

  if (!userId) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold text-secondary-900">Learner not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go back
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isUserError) {
    const message = userError instanceof Error ? userError.message : 'Unable to load user profile.'
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold text-secondary-900">Unable to load learner</p>
        <p className="mt-1 text-sm text-secondary-600">{message}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go back
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(-1)} className="text-secondary-600">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        {user?.id && (
          <Link to={paths.app.users.detail.build(user.id)}>
            <Button variant="ghost" className="text-secondary-600 hover:text-secondary-900">
              <Shield className="mr-2 h-4 w-4" />
              View user profile
            </Button>
          </Link>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={user.fullName || ''}
                className="h-16 w-16 rounded-full object-cover ring-2 ring-primary-100"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-xl font-semibold text-secondary-600">
                {user?.initials || <UserIcon className="h-7 w-7" />}
              </div>
            )}
            <div>
              <CardTitle className="text-2xl text-secondary-900">{user?.fullName ?? 'Unnamed learner'}</CardTitle>
              <CardDescription className="text-secondary-600">
                {user?.programmeTitle || user?.departmentName || 'No programme assigned'}
              </CardDescription>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  {user?.role ?? 'Learner'}
                </Badge>
                <Badge className={cn('rounded-full px-3 py-1 text-xs font-semibold', user?.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-secondary-100 text-secondary-700')}>
                  {user?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-secondary-100 bg-secondary-50 px-4 py-3">
            <p className="text-sm font-medium text-secondary-700">BDSP Licensing</p>
            <p className="text-2xl font-bold text-secondary-900">{licenses.length}</p>
            <p className="text-xs text-secondary-500">Total license tracks</p>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="flex items-start gap-3 rounded-lg bg-secondary-50 px-4 py-3">
            <Calendar className="mt-0.5 h-4 w-4 text-primary-600" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Created</p>
              <p className="text-sm text-secondary-700">{formatDate(user?.createdAt ?? null)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg bg-secondary-50 px-4 py-3">
            <MapPin className="mt-0.5 h-4 w-4 text-primary-600" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Programme</p>
              <p className="text-sm text-secondary-700">{user?.programmeTitle || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg bg-secondary-50 px-4 py-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 text-primary-600" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Status insight</p>
              <p className="text-sm text-secondary-700">
                {licenses.length > 0 ? 'Review licenses below for next action.' : 'No license records yet.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-amber-800">BDSP licensing flow</p>
            <p className="text-sm text-amber-700">
              Learners must have an exam result before licenses appear. After an exam is passed, schedule the interview when due (usually three months later),
              record the outcome, and issue the license if they pass. Use withdraw if a license must be revoked.
            </p>
          </div>
        </div>
      </div>

      {isLicensesError && (
        <div className="rounded-xl border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
          {licensesError instanceof Error ? licensesError.message : 'Unable to load license records.'}
        </div>
      )}

      {isEmpty ? (
        <div className="rounded-2xl border border-dashed border-secondary-200 bg-white p-10 text-center">
          <ShieldCheck className="mx-auto h-12 w-12 text-secondary-300" />
          <h3 className="mt-4 text-xl font-semibold text-secondary-900">No license records yet</h3>
          <p className="mt-2 text-sm text-secondary-600">
            Record the learner&apos;s exam result to create their first BDSP license record. Once the exam is saved, the license will appear here.
          </p>
          <Button className="mt-6" onClick={() => openModal('recordExam')}>
            <ClipboardCheck className="mr-2 h-4 w-4" />
            Record Exam Result
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {licenses.map((license) => {
            const status = license.status ?? null
            const statusLabel = resolveStatusLabel(status)
            const statusClass = resolveStatusClass(status)
            const statusHint = resolveStatusHint(status)
            const cardKey = license.id ?? `${license.programmeId}-${license.createdAt}`

            return (
              <Card key={cardKey} className="border border-secondary-100 shadow-sm">
                <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <CardTitle className="text-xl text-secondary-900">
                      {license.licenseReference
                        ? `License ${license.licenseReference}`
                        : resolveProgrammeName(license.programmeId, license.programmeTitle)}
                    </CardTitle>
                    <CardDescription className="text-secondary-600">
                      Programme: {resolveProgrammeName(license.programmeId, license.programmeTitle)}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={cn('rounded-full px-3 py-1 text-xs font-semibold', statusClass)}>
                      {statusLabel}
                    </Badge>
                    {license.licenseIssued ? (
                      <Badge className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                        <CheckCircle className="mr-1 h-3.5 w-3.5" />
                        Issued
                      </Badge>
                    ) : (
                      <Badge className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-700">
                        <BadgeCheck className="mr-1 h-3.5 w-3.5" />
                        In progress
                      </Badge>
                    )}
                    <Dropdown
                      triggerLabel={
                        <span className="flex items-center gap-2 text-secondary-700">
                          <MoreVertical className="h-4 w-4" />
                          Actions
                        </span>
                      }
                      className="rounded-full border border-secondary-200 px-3 py-1 text-secondary-600 hover:bg-secondary-50"
                      items={licenseActionItems(license)}
                      onSelect={(value) => handleLicenseActionSelect(license, value)}
                      align="end"
                      width="w-60"
                      hideArrow
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-xl border border-secondary-100 bg-secondary-50 px-4 py-3 text-sm text-secondary-700">
                    {statusHint}
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-xl border border-secondary-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Exam</p>
                      <div className="mt-3 space-y-2 text-sm text-secondary-700">
                        <p>Score: <span className="font-medium">{formatScore(license.examScore)}</span></p>
                        <p>Passed: <span className="font-medium">{license.examPassed ? 'Yes' : 'No'}</span></p>
                        <p>Taken on: <span className="font-medium">{formatDate(license.examTakenAt)}</span></p>
                      </div>
                    </div>
                    <div className="rounded-xl border border-secondary-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Interview</p>
                      <div className="mt-3 space-y-2 text-sm text-secondary-700">
                        <p>Scheduled: <span className="font-medium">{formatDate(license.interviewScheduledAt)}</span></p>
                        <p>Score: <span className="font-medium">{formatScore(license.interviewScore)}</span></p>
                        <p>Passed: <span className="font-medium">{license.interviewPassed === null ? '—' : license.interviewPassed ? 'Yes' : 'No'}</span></p>
                        {license.interviewZoomLink && (
                          <p className="truncate">
                            Zoom:{' '}
                            <a href={license.interviewZoomLink} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 underline">
                              {license.interviewZoomLink}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="rounded-xl border border-secondary-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">License</p>
                      <div className="mt-3 space-y-2 text-sm text-secondary-700">
                        <p>Issued: <span className="font-medium">{license.licenseIssued ? 'Yes' : 'No'}</span></p>
                        <p>Issued on: <span className="font-medium">{formatDate(license.licenseIssuedAt)}</span></p>
                        <p>Reference: <span className="font-medium">{license.licenseReference || '—'}</span></p>
                        <p>Notes:</p>
                        <p className="text-xs text-secondary-600">{license.notes || 'No notes recorded.'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}

          <div className="flex items-center justify-end">
            {meta && (
              <Paginator
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={(nextPage) => setPage(Math.max(1, nextPage))}
                onPerPageChange={(next) => {
                  setPerPage(next)
                  setPage(1)
                }}
                isPreviousData={isLicensesFetching}
              />
            )}
          </div>
        </div>
      )}

      {/* Record exam result */}
      <Modal
        isOpen={modalState.type === 'recordExam'}
        onClose={closeModal}
        title="Record exam result"
      >
        <form className="space-y-4" onSubmit={handleRecordExamSubmit}>
          <p className="rounded-lg bg-secondary-50 px-3 py-2 text-sm text-secondary-700">
            Exam results are now recorded directly against this learner. A license record will be created automatically once the score is saved.
          </p>
          {modalError && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {modalError}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-secondary-700">Exam Score (%)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="75"
                {...recordExamForm.register('examScore', {
                  required: 'Exam score is required',
                })}
              />
              {recordExamForm.formState.errors.examScore && (
                <p className="text-sm text-rose-600">{recordExamForm.formState.errors.examScore.message}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-secondary-700">Exam Date</label>
              <Input
                type="date"
                {...recordExamForm.register('examTakenAt')}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button type="submit" disabled={recordExamMutation.isPending}>
              {recordExamMutation.isPending ? 'Saving...' : 'Save exam result'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Schedule interview */}
      <Modal
        isOpen={modalState.type === 'scheduleInterview'}
        onClose={closeModal}
        title="Schedule interview"
      >
        <form className="space-y-4" onSubmit={handleScheduleInterviewSubmit}>
          {modalError && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {modalError}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-secondary-700">Interview Date</label>
              <Input
                type="date"
                min={minInterviewDate}
                {...scheduleInterviewForm.register('scheduledAt', { required: 'Scheduled date is required' })}
              />
              {scheduleInterviewForm.formState.errors.scheduledAt && (
                <p className="text-sm text-rose-600">{scheduleInterviewForm.formState.errors.scheduledAt.message}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-secondary-700">Zoom link (optional)</label>
              <Input
                placeholder="https://zoom.us/..."
                {...scheduleInterviewForm.register('zoomLink')}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button type="submit" disabled={scheduleInterviewMutation.isPending}>
              {scheduleInterviewMutation.isPending ? 'Scheduling...' : 'Schedule interview'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Record interview result */}
      <Modal
        isOpen={modalState.type === 'recordInterview'}
        onClose={closeModal}
        title="Record interview result"
      >
        <form className="space-y-4" onSubmit={handleInterviewResultSubmit}>
          {modalError && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {modalError}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-secondary-700">Interview Score (%)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="80"
                {...interviewResultForm.register('score', { required: 'Interview score is required' })}
              />
              {interviewResultForm.formState.errors.score && (
                <p className="text-sm text-rose-600">{interviewResultForm.formState.errors.score.message}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-secondary-700">Passed</label>
              <Select {...interviewResultForm.register('passed')}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button type="submit" disabled={interviewResultMutation.isPending}>
              {interviewResultMutation.isPending ? 'Saving...' : 'Save result'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Issue license */}
      <Modal
        isOpen={modalState.type === 'issueLicense'}
        onClose={closeModal}
        title="Issue license"
      >
        <form className="space-y-4" onSubmit={handleIssueLicenseSubmit}>
          <p className="rounded-lg bg-secondary-50 px-3 py-2 text-sm text-secondary-700">
            When the interview score is above 70%, the license is usually issued automatically. Use this form to manually trigger issuance.
          </p>
          {modalError && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {modalError}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-secondary-700">Confirm score (optional)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="80"
                {...issueLicenseForm.register('score')}
              />
              {issueLicenseForm.formState.errors.score && (
                <p className="text-sm text-rose-600">{issueLicenseForm.formState.errors.score.message}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-secondary-700">Passed?</label>
              <Select {...issueLicenseForm.register('passed')}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button type="submit" disabled={issueLicenseMutation.isPending}>
              {issueLicenseMutation.isPending ? 'Issuing...' : 'Issue license'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Withdraw license */}
      <Modal
        isOpen={modalState.type === 'withdrawLicense'}
        onClose={closeModal}
        title="Withdraw / revoke license"
      >
        <form className="space-y-4" onSubmit={handleWithdrawLicenseSubmit}>
          {modalError && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {modalError}
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-secondary-700">Reason</label>
            <Textarea
              rows={4}
              placeholder="Explain why the license is being revoked..."
              {...withdrawLicenseForm.register('reason', { required: 'Reason is required' })}
            />
            {withdrawLicenseForm.formState.errors.reason && (
              <p className="text-sm text-rose-600">{withdrawLicenseForm.formState.errors.reason.message}</p>
            )}
          </div>
          <Controller
            control={withdrawLicenseForm.control}
            name="revokeCertificate"
            render={({ field }) => (
              <Checkbox
                label="Revoke issued certificates where applicable"
                checked={field.value}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            )}
          />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="destructive" disabled={withdrawLicenseMutation.isPending}>
              {withdrawLicenseMutation.isPending ? 'Revoking...' : 'Withdraw license'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View license */}
      <Modal
        isOpen={modalState.type === 'viewLicense'}
        onClose={closeModal}
        title="License details"
        size="lg"
      >
        {modalState.license ? (
          <div className="space-y-4 text-sm text-secondary-700">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">License ID</p>
                <p className="text-secondary-900">{modalState.license.id ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Reference</p>
                <p className="text-secondary-900">{modalState.license.licenseReference ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Programme</p>
                <p className="text-secondary-900">
                  {resolveProgrammeName(modalState.license.programmeId, modalState.license.programmeTitle, false)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Status</p>
                <p className="text-secondary-900">
                  {modalState.license.status
                    ? resolveStatusLabel(modalState.license.status)
                    : '—'}
                </p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-secondary-100 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Exam</p>
                <ul className="mt-3 space-y-1">
                  <li>Score: {formatScore(modalState.license.examScore)}</li>
                  <li>Passed: {modalState.license.examPassed ? 'Yes' : 'No'}</li>
                  <li>Recorded: {formatDate(modalState.license.examTakenAt)}</li>
                </ul>
              </div>
              <div className="rounded-xl border border-secondary-100 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Interview</p>
                <ul className="mt-3 space-y-1">
                  <li>Scheduled: {formatDate(modalState.license.interviewScheduledAt)}</li>
                  <li>Score: {formatScore(modalState.license.interviewScore)}</li>
                  <li>Passed: {modalState.license.interviewPassed === null ? '—' : modalState.license.interviewPassed ? 'Yes' : 'No'}</li>
                  <li>Link: {modalState.license.interviewZoomLink || '—'}</li>
                </ul>
              </div>
            </div>
            <div className="rounded-xl border border-secondary-100 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Notes</p>
              <p className="mt-2 text-secondary-700">{modalState.license.notes || 'No notes recorded for this license.'}</p>
            </div>
            <div className="flex justify-end">
              <Button onClick={closeModal}>Close</Button>
            </div>
          </div>
        ) : (
          <p className="text-secondary-600">No license selected.</p>
        )}
      </Modal>
    </div>
  )
}
